/**
 * ShieldSatellite.ts
 * Pooled hostile satellite for the World 2 space shmup.
 *
 * Behaviour (this slice): linear leftward drift in world space +
 * sinusoidal vertical "orbit" wobble around its spawn Y. 3 HP — the
 * tankiest non-boss enemy in the slice. Spawned at the right edge of
 * the camera, orbits past the ship, and despawns once its right edge
 * crosses the `SPACE_ENEMY_OFFSCREEN_MARGIN` past the left edge of the
 * camera.
 *
 * Directional shield: the satellite is immune to hits whose impact-X
 * lands on the front half of its hitbox (the LEFT half in world space,
 * since the satellite drifts LEFT — the front IS the direction of
 * motion). The rear half (RIGHT half) takes damage normally. In a
 * left-scrolling shmup where the ship always fires RIGHT, this means
 * direct laser fire is absorbed and the only practical way to chip a
 * satellite is missile AoE whose epicenter is to the satellite's right
 * (e.g. detonate the missile on an asteroid or drone behind the
 * satellite). When the caller does not pass `hitX` (no positional
 * context), the satellite falls back to the base "any damage works"
 * rule so unit-level pool / state tests still pass.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module ShieldSatellite
 */

import { SpaceEnemyBase } from './SpaceEnemyBase';
import { spaceState } from '../engine/spaceState';
import type { LevelRuntime } from '../engine/gameState';
import {
  SHIELD_SATELLITE_WIDTH,
  SHIELD_SATELLITE_HEIGHT,
  SHIELD_SATELLITE_HP,
  SHIELD_SATELLITE_DRIFT_SPEED,
  SHIELD_SATELLITE_WOBBLE_AMPLITUDE,
  SHIELD_SATELLITE_WOBBLE_FREQUENCY_HZ,
  SPACE_ENEMY_OFFSCREEN_MARGIN,
  type SpaceEnemyKind,
} from '../../constants/spaceEnemyConstants';

/** Angular frequency (rad/s) for the wobble — `2π · f`. */
const WOBBLE_OMEGA = 2 * Math.PI * SHIELD_SATELLITE_WOBBLE_FREQUENCY_HZ;

/**
 * Sinusoidal-orbit shield satellite. Pooled — use after `acquire()` from
 * the satellite pool in `spaceEnemySystem`.
 */
export class ShieldSatellite extends SpaceEnemyBase {
  /** Discriminator read by the renderer to pick a colour / sprite. */
  public readonly kind: SpaceEnemyKind = 'satellite';

  /**
   * Spawn-Y the wobble orbits around. The base `y` field tracks the
   * current world position; `centerY` is the equilibrium of the sine.
   */
  private centerY = 0;

  /** Elapsed seconds since spawn, used as the sine phase. */
  private wobbleT = 0;

  constructor() {
    super(SHIELD_SATELLITE_WIDTH, SHIELD_SATELLITE_HEIGHT);
  }

  /**
   * Initialise the satellite at a world position. Marks it live, assigns
   * a fresh `spawnId` via the base class, and seeds the orbit centre.
   *
   * @param x - Spawn X (world space, top-left of hitbox).
   * @param y - Spawn Y (world space, top-left of hitbox). Treated as the
   *            equilibrium of the vertical wobble.
   */
  public spawn(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.vx = -SHIELD_SATELLITE_DRIFT_SPEED;
    this.vy = 0;
    this.hp = SHIELD_SATELLITE_HP;
    this.centerY = y;
    this.wobbleT = 0;
    this.markSpawned();
  }

  /**
   * Pool-reuse hook — `BaseEntity.reset()` zeros position / velocity /
   * HP; override here to also clear the wobble state so a re-acquired
   * instance does not inherit a partial sine phase from its prior life.
   */
  public override reset(): void {
    super.reset();
    this.centerY = 0;
    this.wobbleT = 0;
  }

  /**
   * Apply `amount` damage with a directional-shield gate. The shield
   * covers the FRONT half of the hitbox (the LEFT half in world space —
   * the satellite drifts LEFT so its front IS the LEFT face). A hit
   * whose `hitX` lands left of the hitbox centre is absorbed (no HP
   * decrement, returns `false`); a hit on or right of the centre damages
   * via the base impl.
   *
   * `hitX` is conventionally the projectile's centre at first overlap
   * for laser bolts and missile direct hits, and the explosion epicentre
   * for missile splash damage. Callers that don't have a positional
   * source (e.g. unit tests, future ram-collision damage with no clean
   * impact point) can omit `hitX` to fall through to the base "any
   * damage works" rule — the shield is a render-vs-collision concern,
   * not a hard "satellites cannot lose HP" rule.
   *
   * DECISION: Half-plane shield uses `hitX < centreX` (strict less-than)
   * so a hit at the exact centre belongs to the rear and damages. The
   * default missile detonation against an open-space satellite lands its
   * epicentre at the missile centre, which on first overlap sits just
   * inside the satellite's LEFT face — well under the centre. Strict-
   * less is the conservative choice for the dividing pixel without
   * having to add an `EPSILON` constant.
   *
   * @param amount - Damage in HP units. Non-positive ignored.
   * @param hitX   - World-space X of the impact. When omitted, the
   *                 shield is bypassed and damage is applied straight
   *                 through (back-compat for any callers that don't yet
   *                 pass positional context).
   * @param hitY   - Unused — `void`ed so the override matches the base
   *                 signature exactly.
   * @returns `true` if the satellite was killed by this hit.
   */
  public override takeDamage(
    amount: number,
    hitX?: number,
    hitY?: number,
  ): boolean {
    void hitY;
    if (this.dead || amount <= 0) return false;
    if (hitX !== undefined) {
      const centreX = this.x + this.width / 2;
      if (hitX < centreX) {
        // FRONT half — shield absorbs. Projectile is consumed by the
        // caller's collision pipeline either way; we just refuse the HP
        // decrement here.
        return false;
      }
    }
    return super.takeDamage(amount);
  }

  /**
   * Advance one fixed timestep. Linear leftward drift + sinusoidal Y
   * around `centerY`. Camera-relative left cull mirrors the other space
   * enemies so they all leave the world at a consistent x position.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.x += this.vx * dt;
    this.wobbleT += dt;
    // DECISION: `vy` is left at 0 because Y is driven directly by the
    // sine — the wobble is a kinematic curve, not an integrated velocity.
    // Keeping `vy = 0` means future systems that read `vy` for knockback
    // or hit-pause see a coherent value rather than a transient sample of
    // the sine derivative.
    this.y = this.centerY + SHIELD_SATELLITE_WOBBLE_AMPLITUDE * Math.sin(WOBBLE_OMEGA * this.wobbleT);

    const leftCullX = spaceState.cameraX - SPACE_ENEMY_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
